import { useEffect, useRef, useState } from 'react';
import { HocuspocusProvider } from '@hocuspocus/provider';
import { clearDocument } from 'y-indexeddb';
import { createLiveYDoc } from './create-live-ydoc.js';
import { ydocToJSON } from './ydoc-to-json.js';
const applyYdocToEditor = (ydoc, editor, onRemoteContentApplied) => {
    if (editor.isDestroyed) {
        return false;
    }
    const json = ydocToJSON(ydoc);
    if (!json) {
        return false;
    }
    editor.commands.setContent(json, { emitUpdate: false });
    onRemoteContentApplied?.();
    return true;
};
export const useReceiveOnlyDocumentSync = ({ documentId, token, websocketUrl, editor, enabled = true, pauseApplying = false, onRemoteContentApplied, onStateless, clearLocalCacheOnStart = true, ydocRef: externalYdocRef, }) => {
    const [isSynced, setIsSynced] = useState(false);
    const editorRef = useRef(editor);
    const pauseApplyingRef = useRef(pauseApplying);
    const onAppliedRef = useRef(onRemoteContentApplied);
    const onStatelessRef = useRef(onStateless);
    const internalYdocRef = useRef(null);
    const ydocRef = externalYdocRef ?? internalYdocRef;
    const pendingApplyRef = useRef(false);
    editorRef.current = editor;
    pauseApplyingRef.current = pauseApplying;
    onAppliedRef.current = onRemoteContentApplied;
    onStatelessRef.current = onStateless;
    const tryApplyRemoteContent = () => {
        const currentYdoc = ydocRef.current;
        const currentEditor = editorRef.current;
        if (!currentYdoc || currentYdoc.isDestroyed || !currentEditor || currentEditor.isDestroyed) {
            pendingApplyRef.current = true;
            return;
        }
        if (pauseApplyingRef.current) {
            pendingApplyRef.current = true;
            return;
        }
        pendingApplyRef.current = false;
        applyYdocToEditor(currentYdoc, currentEditor, onAppliedRef.current);
    };
    useEffect(() => {
        if (!enabled || !token || !documentId || documentId === 'new') {
            setIsSynced(false);
            return undefined;
        }
        let cancelled = false;
        let provider = null;
        const ydoc = createLiveYDoc();
        ydocRef.current = ydoc;
        const handleUpdate = () => {
            if (!cancelled) {
                tryApplyRemoteContent();
            }
        };
        const start = async () => {
            if (clearLocalCacheOnStart && typeof window !== 'undefined') {
                try {
                    await clearDocument(documentId);
                }
                catch (error) {
                    console.warn('[ReceiveOnlySync] Failed to clear local document cache:', error);
                }
            }
            if (cancelled) {
                ydoc.destroy();
                return;
            }
            ydoc.on('update', handleUpdate);
            provider = new HocuspocusProvider({
                url: websocketUrl,
                name: documentId,
                document: ydoc,
                token,
            });
            const handleStateless = ({ payload }) => {
                if (!cancelled) {
                    onStatelessRef.current?.(payload);
                }
            };
            provider.on('synced', () => {
                if (cancelled) {
                    return;
                }
                setIsSynced(true);
                tryApplyRemoteContent();
            });
            provider.on('disconnect', () => {
                if (!cancelled) {
                    setIsSynced(false);
                }
            });
            provider.on('stateless', handleStateless);
        };
        void start();
        return () => {
            cancelled = true;
            ydoc.off('update', handleUpdate);
            provider?.off('stateless');
            provider?.destroy();
            if (!ydoc.isDestroyed) {
                ydoc.destroy();
            }
            if (ydocRef.current === ydoc) {
                ydocRef.current = null;
            }
            setIsSynced(false);
            pendingApplyRef.current = false;
        };
    }, [documentId, token, websocketUrl, enabled, clearLocalCacheOnStart]);
    useEffect(() => {
        if (pauseApplying || !pendingApplyRef.current) {
            return;
        }
        tryApplyRemoteContent();
    }, [pauseApplying, editor, isSynced]);
    return { isSynced, ydocRef };
};
//# sourceMappingURL=use-receive-only-document-sync.js.map